import { useEffect, useRef } from 'react'

import { ExitType } from './events'
import type { CoreStepKey, StepTiming } from './events'
import { trackBeacon } from './track'

type Options = {
  /**
   * The step the user is on, or null once the journey is over.
   *
   * One prop rather than a `step` plus an `enabled` flag: with two, the disabled
   * state still has to name a step, and whatever it names is a lie. Null says
   * "nothing to report" and leaves no invented value behind.
   */
  step: CoreStepKey | null
  getTiming: () => StepTiming
}

/**
 * Reports leaving a step, and leaving the flow.
 *
 * BR-39 requires that closing or navigating away is captured with the step and
 * the time spent on it. The upstream spec puts the whole thing on
 * `visibilitychange`; that is right about the transport — a request fired from
 * `unload` is dropped by the browser, so `sendBeacon` is the only reliable path
 * — but wrong about the meaning. `visibilitychange → hidden` also fires when
 * somebody switches tab to read an email and comes straight back, which is not
 * abandonment. Reporting it as one would make the drop-off numbers useless.
 *
 * So the two are separated:
 *   - `onboarding_step_exited` on every hide, carrying `tab_hidden`. This is the
 *     raw signal, and it is what makes the focused/wall gap interpretable.
 *   - `onboarding_abandoned` once per mount, on `pagehide` or on leaving the
 *     route inside the app.
 *
 * True abandonment is then a question the dashboard answers — "reached this
 * step, never reached `onboarding_completed`" — which funnels express natively.
 */
export const useExitBeacon = ({ step, getTiming }: Options): void => {
  /**
   * Latest props, read by listeners registered once. Without this the
   * `pagehide` handler would close over the step the user was on when the
   * listener was attached and report that one instead.
   *
   * Updated from an effect, not during render — a ref written in render is
   * unsafe under concurrent rendering, and the listeners only ever read it after
   * a commit anyway.
   */
  const latest = useRef({ step, getTiming })

  useEffect(() => {
    latest.current = { step, getTiming }
  }, [step, getTiming])

  /** One abandonment per mount — `pagehide` can fire more than once. */
  const abandoned = useRef(false)

  /**
   * Pending "they navigated away" report, cancelled if the effect runs again.
   *
   * StrictMode mounts, unmounts and remounts every component, and the unmount is
   * indistinguishable from a real one — reporting straight from the cleanup
   * fabricated an `onboarding_abandoned` about 200ms after every mount, on the
   * step the user had only just arrived at. Deferring by a tick and letting the
   * remount cancel it tells the two apart: a genuine unmount has nothing to
   * cancel it, so it still fires.
   */
  const pendingExit = useRef<number>(undefined)

  useEffect(() => {
    window.clearTimeout(pendingExit.current)

    const abandon = (
      exit_type: typeof ExitType.PAGE_HIDDEN | typeof ExitType.NAVIGATED_AWAY
    ) => {
      const { step: current, getTiming: timing } = latest.current
      if (current === null || abandoned.current) return

      abandoned.current = true
      trackBeacon('onboarding_abandoned', {
        step: current,
        exit_type,
        ...timing()
      })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return

      const { step: current, getTiming: timing } = latest.current
      if (current === null) return

      trackBeacon('onboarding_step_exited', {
        step: current,
        exit_type: ExitType.TAB_HIDDEN,
        ...timing()
      })
    }

    const onPageHide = () => abandon(ExitType.PAGE_HIDDEN)

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', onPageHide)

      // Unmounting while the journey is still open means the user went somewhere
      // else in the app — the one exit route `pagehide` never sees. Deferred so a
      // StrictMode remount can take it back; see `pendingExit`.
      pendingExit.current = window.setTimeout(
        () => abandon(ExitType.NAVIGATED_AWAY),
        0
      )
    }
  }, [])
}
